/**
 * US jurisdictions for the distributor tax-nexus selector.
 *
 * Single source of truth for the 51 rows (50 states + DC) rendered by
 * `TaxNexusEditor.vue`. The `name` strings are the FULL state names and
 * MUST match the backend canonical list in
 * `api/src/helper_tables/us_state_codes.csv` (read by
 * `get_state_name(...)` in `api/src/reports/helpers.py`), because the
 * stored `distributors.nexus_states` JSON column and the sales-tax
 * consumer (`api/src/endpoints/sales_tax.py`) compare against those exact
 * full-name strings. Abbreviations here are for display/keys only; the
 * persisted value is always the full name.
 *
 * `region` groups jurisdictions into the four US Census regions
 * (Northeast / Midwest / South / West). DC is grouped under South to
 * match Census Bureau convention. Regions are used only as optional
 * table section headers with a "select all in region" control; they do
 * NOT change the single-table-with-checkboxes interaction model.
 */

export const US_JURISDICTIONS = [
    // Northeast
    { name: "Connecticut", code: "CT", region: "Northeast" },
    { name: "Maine", code: "ME", region: "Northeast" },
    { name: "Massachusetts", code: "MA", region: "Northeast" },
    { name: "New Hampshire", code: "NH", region: "Northeast" },
    { name: "New Jersey", code: "NJ", region: "Northeast" },
    { name: "New York", code: "NY", region: "Northeast" },
    { name: "Pennsylvania", code: "PA", region: "Northeast" },
    { name: "Rhode Island", code: "RI", region: "Northeast" },
    { name: "Vermont", code: "VT", region: "Northeast" },

    // Midwest
    { name: "Illinois", code: "IL", region: "Midwest" },
    { name: "Indiana", code: "IN", region: "Midwest" },
    { name: "Iowa", code: "IA", region: "Midwest" },
    { name: "Kansas", code: "KS", region: "Midwest" },
    { name: "Michigan", code: "MI", region: "Midwest" },
    { name: "Minnesota", code: "MN", region: "Midwest" },
    { name: "Missouri", code: "MO", region: "Midwest" },
    { name: "Nebraska", code: "NE", region: "Midwest" },
    { name: "North Dakota", code: "ND", region: "Midwest" },
    { name: "Ohio", code: "OH", region: "Midwest" },
    { name: "South Dakota", code: "SD", region: "Midwest" },
    { name: "Wisconsin", code: "WI", region: "Midwest" },

    // South (DC grouped here per US Census convention)
    { name: "Alabama", code: "AL", region: "South" },
    { name: "Arkansas", code: "AR", region: "South" },
    { name: "Delaware", code: "DE", region: "South" },
    { name: "District of Columbia", code: "DC", region: "South" },
    { name: "Florida", code: "FL", region: "South" },
    { name: "Georgia", code: "GA", region: "South" },
    { name: "Kentucky", code: "KY", region: "South" },
    { name: "Louisiana", code: "LA", region: "South" },
    { name: "Maryland", code: "MD", region: "South" },
    { name: "Mississippi", code: "MS", region: "South" },
    { name: "North Carolina", code: "NC", region: "South" },
    { name: "Oklahoma", code: "OK", region: "South" },
    { name: "South Carolina", code: "SC", region: "South" },
    { name: "Tennessee", code: "TN", region: "South" },
    { name: "Texas", code: "TX", region: "South" },
    { name: "Virginia", code: "VA", region: "South" },
    { name: "West Virginia", code: "WV", region: "South" },

    // West
    { name: "Alaska", code: "AK", region: "West" },
    { name: "Arizona", code: "AZ", region: "West" },
    { name: "California", code: "CA", region: "West" },
    { name: "Colorado", code: "CO", region: "West" },
    { name: "Hawaii", code: "HI", region: "West" },
    { name: "Idaho", code: "ID", region: "West" },
    { name: "Montana", code: "MT", region: "West" },
    { name: "Nevada", code: "NV", region: "West" },
    { name: "New Mexico", code: "NM", region: "West" },
    { name: "Oregon", code: "OR", region: "West" },
    { name: "Utah", code: "UT", region: "West" },
    { name: "Washington", code: "WA", region: "West" },
    { name: "Wyoming", code: "WY", region: "West" },
];

// The four Census regions, in display order.
export const US_REGIONS = ["Northeast", "Midwest", "South", "West"];

/**
 * Stable slug for a jurisdiction, used to build `data-testid` values.
 * "New York" -> "new-york", "District of Columbia" -> "district-of-columbia".
 * Pure, deterministic, ASCII-only.
 */
export function jurisdictionSlug(name) {
    return String(name)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

/**
 * `data-testid` for a jurisdiction's row checkbox.
 * e.g. "California" -> "nexus-row-california".
 */
export function jurisdictionTestid(name) {
    return `nexus-row-${jurisdictionSlug(name)}`;
}

/**
 * `data-testid` for a region's "select all" control.
 * e.g. "Northeast" -> "nexus-region-northeast".
 */
export function regionTestid(region) {
    return `nexus-region-${jurisdictionSlug(region)}`;
}

/**
 * Canonical full name for a stored/incoming nexus entry.
 *
 * A 2-letter code ("OR") resolves to its full name ("Oregon"); a full name,
 * or anything unrecognised, is returned unchanged (never invents a state).
 * The sales-tax engine compares FULL names only, so every writer of
 * `distributors.nexus_states` must persist the output of this function.
 */
export function nexusStateName(value) {
    if (typeof value !== "string") return value;
    const match = US_JURISDICTIONS.find((j) => j.code === value);
    return match ? match.name : value;
}

/**
 * Normalize a list of nexus entries for display or write: each entry mapped
 * through `nexusStateName`, de-duplicated, sorted. Null / non-array -> [].
 */
export function canonicalNexusNames(list) {
    if (!Array.isArray(list)) return [];
    return [...new Set(list.map(nexusStateName))].sort();
}
